/**
 * MuseStrip — Ovid's `HouseTheme.identityPanel`. Lives entirely in the bar's own slot now (plan
 * `2026_09_18_armed_frame_budget.md` § D6, board `OvidMuseFlow.dc.html`'s `.bar`/`.muses`/`.thumbs`):
 * one call, deciding its own shape from `open`/`identity` alone, same as the chip/panel split it
 * replaces (`2026_09_18_ovid_wall_polish.md` § P4) — no separate standing slot above the bar any
 * more.
 *
 * Closed (a muse chosen, the strip not asked for): 40px square thumb, her name, a chevron, one
 * button, no "As", no "Change" (a tap reopens the strip in place). The chevron and the thumb's
 * hover/focus outline (operator ruling, 2026-09-18) are the chip's only signifiers that it presses —
 * Render stays the bar's one filled element, the chip gains no fill, border or pill of its own.
 *
 * Open (museless always, or the chip was tapped, or a `/continue` refusal asked for it): a single
 * horizontally-scrolling row of all muses, the chosen one first (`orderForStrip`), 44x58 thumbs, no
 * name captions (the board's open state carries none — only the closed chip names her), the
 * HIGHLIGHTED one (hovered, keyboard-focused, else the chosen one) outlined in the house accent.
 * After the fifth thumb a `+N` door scrolls the row by one page; it hides once the row has nothing
 * further to reveal. No "chance", no "all N →", no heading, no Done button. Choosing folds it at once
 * (optimistically — a refusal reopens it with its error); the host folds it on Escape or a press
 * outside it.
 *
 * The frame previews the highlighted muse's hero while the strip is open (`onPreview`,
 * `IdentityPanelProps`) — cleared the moment the strip closes or this component unmounts, so core
 * never has to guess when a preview is stale.
 *
 * The chosen muse's own picture rides on the identity (`Identity.thumb`), so the closed chip never fetches the
 * roster to show one picture. The rest of the data and thumbnails come from `GET /mine/muses` (`routes/mine.muses.ts`), NOT a static import of
 * `muses/registry.ts` (review item D, adversarial pass on wall verbs, 2026-09-17): the registry
 * carries 65 KB of casting clauses, SEO titles, editorial stories and pack slugs, and a static
 * import would ship it inside the anonymous home's own client chunk (`theme.tsx` lazy-loads this
 * file for the same reason). `MuseFrame`'s onError-degrades-to-ground idiom is reused verbatim.
 *
 * `POST /mine/muse` (the wire this file owns, unchanged by this pass) is shared with `/muse`'s
 * existing `choose-muse` action per plan `2026_09_17_ovid_wall_verbs.md` § Decided 6.
 *
 * A muse of her own is just another muse in the strip (plan `2026_10_09_ovid_first_offer.md` § Design, boards
 * `OvidOwnMuseFlow.dc.html` and `OvidMobileOwnMuse.dc.html`): a dashed + frame heads the open strip and opens the
 * wall's intake; her own muses follow it, a hairline, then the roster. While one is coming to life the closed slot is
 * hers (her photo, the line, the hairline). An own thumb that can go carries a small x (a long press on a phone); the
 * bar then asks once, "Let her go?", and the verb gives way to the two answers. Letting her go is `/muse`'s own
 * `delete-muse` action.
 */
import {useCallback, useEffect, useRef, useState} from 'react'
import {useFetcher} from 'react-router'
import type {Identity} from '@core/wall/contract'
import type {IdentityPanelProps} from '@core/wall/ui/theme'
import {MuseFrame} from '@product/ovid/components/MuseFrame/MuseFrame'
import type {MuseRosterEntry} from '@product/ovid/routes/mine.muses'
import {ovidTrack} from '@product/ovid/utils/analytics'
import {parseOwnMuseKey} from '@product/ovid/muses/museKey'
import {MUSES_CHANGED_EVENT} from './museEvents'
import {trainingLine} from './trainingLine'
import styles from './MuseStrip.module.scss'

// Same-origin, matching the fallback-route pattern every other wall picture uses
// (`routes/muse-samples.$slug.$file.ts`) — a pure naming convention (no registry text), safe to
// build client-side once a slug is known (the chip's own, or the highlighted thumb's). The full
// roster comes from `GET /mine/muses` instead — see this file's own header doc.
function heroUrl(slug: string): string {
    return `/muse-samples/${slug}/hero.webp`
}

/**
 * The identity to hold after a `POST /mine/muse` 200 — the response's own `identity` field when it
 * carries one (the server's own truth, including whatever `name` it resolved — `mine.muse.ts`
 * reads it off `getMuse`, which may differ from the tapped card's own display name in a race), else
 * the optimistic value this file already applied (a body-less or differently-shaped 200 is still a
 * success, per the brief's "200) or an error" — nothing here treats an unexpected 200 shape as a
 * failure the way `core/wall/contract.ts`'s own `isTake` treats a malformed take).
 */
export function chosenIdentity(slug: string, name: string, thumb: string | null, body: unknown): Identity {
    if (body && typeof body === 'object' && 'identity' in body) {
        const next = (body as {identity?: unknown}).identity
        if (next && typeof next === 'object' && (next as {kind?: unknown}).kind === 'muse') {
            return next as Identity
        }
    }
    return {kind: 'muse', slug, name, thumb}
}

/**
 * The strip's own order (plan § D6: "chosen one first") — a pure function of the fetched roster and
 * the chosen slug, so the ordering is testable without a fetch or a render. A chosen slug the roster
 * does not (yet) carry — the roster hasn't loaded, or a stale identity — leaves the order untouched
 * rather than guessing.
 */
export function orderForStrip(roster: readonly MuseRosterEntry[], chosenSlug: string | null): MuseRosterEntry[] {
    if (!chosenSlug) return [...roster]
    const chosen = roster.find((m) => m.slug === chosenSlug)
    if (!chosen) return [...roster]
    return [chosen, ...roster.filter((m) => m.slug !== chosenSlug)]
}

/**
 * The strip as the board draws it: her own muses first, then the roster, a hairline between the two. The order inside
 * each half is the order given (`orderForStrip`'s chosen-first included).
 */
export function splitStrip(ordered: readonly MuseRosterEntry[]): {own: MuseRosterEntry[]; roster: MuseRosterEntry[]} {
    return {own: ordered.filter((m) => m.own === true), roster: ordered.filter((m) => m.own !== true)}
}

/** Who can be let go from the strip: one of her own, finished or failed. One still coming to life runs to its end
 *  (`/muse` offers the same). */
export function canLetGo(muse: MuseRosterEntry): boolean {
    return muse.own === true && muse.state !== 'training'
}

/** The muse of hers that is coming to life now, if any: the bar's closed slot shows her. */
export function trainingMuse(roster: readonly MuseRosterEntry[] | null): MuseRosterEntry | null {
    return roster?.find((m) => m.own === true && m.state === 'training') ?? null
}

/** The bar's closed slot while one of hers is coming to life: her photo, the line, the hairline. A tap (`onOpen`)
 *  opens the strip; the proof page draws the same chip still, with no `onOpen`. */
export function TrainingChip({muse, onOpen}: {muse: Pick<MuseRosterEntry, 'name' | 'percent' | 'thumb'>; onOpen?: () => void}) {
    return (
        <button
            type="button"
            className={`${styles.chip} ${styles.training}`}
            onClick={onOpen}
            aria-haspopup="true"
            aria-expanded={false}
            aria-label={`${trainingLine(muse)}. Change muse`}
        >
            <MuseFrame src={muse.thumb} alt="" className={styles.chipThumb} />
            <span className={styles.prog}>
                <span className={styles.progLine}>{trainingLine(muse)}</span>
                <span className={styles.progTrack} aria-hidden="true">
                    <i style={{width: `${muse.percent ?? 0}%`}} />
                </span>
            </span>
        </button>
    )
}

/** After this many thumbs, the `+N` door stands instead (board `OvidMuseFlow.dc.html`'s `.muses`:
 *  six thumbs then `.more`/`.chance` — the plan drops chance and keeps the door at the fifth). */
const STRIP_PAGE = 5

/** The roster is read again this often while one of hers is coming to life. */
const TRAINING_POLL_MS = 20_000

/** A touch held this long on one of her own thumbs asks to let her go. */
const LONG_PRESS_MS = 550

type DeleteMuseResponse = {status: 'deleted'} | {status: 'error'; error: string}

export function MuseStrip({identity, onIdentityChange, open, onOpenChange, onPreview, onOpenIntake, onHoldBar}: IdentityPanelProps) {
    // Not a muse identity at all (anon, or a response from before this field existed) — `Column`/
    // `Sheet` already read `identity` through `identityArmable` for the verb; this panel simply has
    // nothing to draw. Kept AFTER the hooks below (never a conditional hook call).
    const isMuse = identity.kind === 'muse'
    const chosenSlug = isMuse ? identity.slug : null
    const chosenName = isMuse ? identity.name : null
    const chosenThumb = isMuse ? identity.thumb ?? null : null

    const [pendingSlug, setPendingSlug] = useState<string | null>(null)
    const [failed, setFailed] = useState(false)
    const [roster, setRoster] = useState<MuseRosterEntry[] | null>(null)
    const [highlighted, setHighlighted] = useState<string | null>(null)
    const [atEnd, setAtEnd] = useState(false)
    /** The own muse the bar is asking about ("Let her go?"), or null. */
    const [asked, setAsked] = useState<MuseRosterEntry | null>(null)
    const trackRef = useRef<HTMLDivElement>(null)
    const mountedRef = useRef(true)
    const longPressRef = useRef<{timer: ReturnType<typeof setTimeout> | null; fired: boolean}>({timer: null, fired: false})
    const deleteFetcher = useFetcher<DeleteMuseResponse>()
    const letting = deleteFetcher.state !== 'idle'

    // Museless always shows the strip (plan § D6: every render needs a muse pinned to the row); a
    // real choice shows it only while the reader (or a refusal) asked for it — `open`, `Column`'s/
    // `Sheet`'s own boolean, not a local `expanded`. A question in the bar keeps it showing.
    const showStrip = !chosenSlug || open || asked !== null

    useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
        }
    }, [])

    // The roster is read once when the panel mounts — it says whether one of hers is coming to life, which the closed
    // slot shows — and again when the intake queues a muse (`MUSES_CHANGED_EVENT`) or one is let go. A failed read
    // leaves it `null`: the strip renders empty rather than throwing, and the next read tries again.
    const refreshRoster = useCallback(async () => {
        try {
            const res = await fetch('/mine/muses')
            if (!res.ok) return
            const data: unknown = await res.json()
            if (mountedRef.current && Array.isArray(data)) setRoster(data as MuseRosterEntry[])
        } catch {
            // Left as it was.
        }
    }, [])
    useEffect(() => {
        void refreshRoster()
        window.addEventListener(MUSES_CHANGED_EVENT, refreshRoster)
        return () => window.removeEventListener(MUSES_CHANGED_EVENT, refreshRoster)
    }, [refreshRoster])

    const training = trainingMuse(roster)
    useEffect(() => {
        if (!training) return
        const timer = setInterval(() => {
            if (document.visibilityState === 'visible') void refreshRoster()
        }, TRAINING_POLL_MS)
        return () => clearInterval(timer)
    }, [training?.slug, refreshRoster]) // eslint-disable-line react-hooks/exhaustive-deps -- the key stands for the muse

    // The question holds the bar: the verb leaves it until she is kept or let go.
    useEffect(() => {
        onHoldBar?.(asked !== null)
        return () => onHoldBar?.(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `onHoldBar` is the host's own state setter
    }, [asked])

    // The delete settled: she is gone from the roster, and from the choice if she was chosen.
    const lastSettledRef = useRef<DeleteMuseResponse | null>(null)
    useEffect(() => {
        if (deleteFetcher.state !== 'idle' || !deleteFetcher.data || lastSettledRef.current === deleteFetcher.data) return
        lastSettledRef.current = deleteFetcher.data
        if (deleteFetcher.data.status === 'deleted') {
            ovidTrack('model_deleted', {})
            if (asked && asked.slug === chosenSlug) onIdentityChange({kind: 'muse', slug: null, name: null})
            setAsked(null)
            void refreshRoster()
        }
    }, [deleteFetcher.state, deleteFetcher.data, asked, chosenSlug, onIdentityChange, refreshRoster])

    // The frame previews the highlighted muse's hero while the strip is open (§ D6) — cleared the
    // instant the strip closes or this component unmounts, so the frame never keeps showing a
    // preview for a panel that is no longer on screen. `onPreview` is the caller's own state setter,
    // stable across renders (`Column`/`Sheet` never recreate it) — not a dependency this effect
    // needs to re-fire for.
    useEffect(() => {
        if (!showStrip) {
            onPreview(null)
            return
        }
        const slug = highlighted ?? chosenSlug
        onPreview(slug ? thumbFor(slug) : null)
        return () => onPreview(null)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showStrip, highlighted, chosenSlug])

    // The chosen muse's picture is on the identity; a roster muse's hero is a naming convention; another
    // of hers is the picture the roster carries.
    function thumbFor(slug: string): string | null {
        if (slug === chosenSlug && chosenThumb) return chosenThumb
        const entry = roster?.find((m) => m.slug === slug)
        if (entry) return entry.thumb || null
        return parseOwnMuseKey(slug) === null ? heroUrl(slug) : null
    }

    if (!isMuse) return null

    async function choose(slug: string, name: string, thumb: string | null) {
        if (pendingSlug) return
        setFailed(false)
        setPendingSlug(slug)
        const previous = identity
        // Optimistic, and folded at once. While the strip stayed open through the POST, the chosen
        // thumb jumped to first place under the pointer (`orderForStrip`) and every thumb stood
        // disabled under a forbidden cursor until the server answered — dev look, 2026-09-18. A
        // refusal below puts the previous identity back and reopens the strip with its error.
        onIdentityChange({kind: 'muse', slug, name, thumb})
        onOpenChange(false)
        try {
            const res = await fetch('/mine/muse', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({slug}),
            })
            if (!res.ok) {
                onIdentityChange(previous)
                setFailed(true)
                onOpenChange(true)
                return
            }
            let body: unknown = null
            try {
                body = await res.json()
            } catch {
                // A body-less 200 is still a success — the optimistic value stands (`chosenIdentity`).
            }
            onIdentityChange(chosenIdentity(slug, name, thumb, body))
            // Same event the /muse chooser fires, a new `source` for the wall's own call site
            // (`utils/analytics.ts`'s `muse_chosen`).
            ovidTrack('muse_chosen', {slug, source: 'wall'})
        } catch {
            onIdentityChange(previous)
            setFailed(true)
            onOpenChange(true)
        } finally {
            setPendingSlug(null)
        }
    }

    function letGo(muse: MuseRosterEntry) {
        const modelId = parseOwnMuseKey(muse.slug)
        if (!modelId) return
        const body = new FormData()
        body.append('intent', 'delete-muse')
        body.append('modelId', modelId)
        void deleteFetcher.submit(body, {method: 'post', action: '/muse'})
    }

    function handleScroll() {
        const el = trackRef.current
        if (!el) return
        setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1)
    }

    function scrollMore() {
        trackRef.current?.scrollBy({left: trackRef.current.clientWidth, behavior: 'smooth'})
    }

    // A touch held on one of her own thumbs asks to let her go; the click that follows the release is not a choice.
    function pressStart(muse: MuseRosterEntry, event: React.PointerEvent) {
        if (event.pointerType !== 'touch' || !canLetGo(muse)) return
        longPressRef.current.fired = false
        longPressRef.current.timer = setTimeout(() => {
            longPressRef.current.fired = true
            setAsked(muse)
        }, LONG_PRESS_MS)
    }

    function pressEnd() {
        if (longPressRef.current.timer) clearTimeout(longPressRef.current.timer)
        longPressRef.current.timer = null
    }

    if (!showStrip) {
        if (training && !chosenSlug) {
            // Being made, and no muse chosen to render with: the closed slot is hers — her photo, the line, the
            // hairline. A tap opens the strip, where she stands among the others.
            return <TrainingChip muse={training} onOpen={() => onOpenChange(true)} />
        }
        // The closed chip (P4's shape, 2026-09-18 signifiers added): 40px square thumb, her name, a
        // chevron — one button, no "As", no "Change". `aria-haspopup`/`aria-expanded` name what the
        // tap does; the visible label stays her name, not a "Change muse" word (decision 3).
        if (!chosenSlug) return null
        // The chip names who the verb renders with, always. One of hers being made rides under that name as a
        // quiet line and its hairline: a status of a muse to come, never a second subject in the bar.
        return (
            <button
                type="button"
                className={styles.chip}
                onClick={() => onOpenChange(true)}
                aria-haspopup="true"
                aria-expanded={false}
                aria-label={`Change muse: ${chosenName}${training ? `. ${trainingLine(training)}` : ''}`}
            >
                <MuseFrame src={thumbFor(chosenSlug) ?? ''} alt="" className={styles.chipThumb} />
                {training ? (
                    <span className={styles.chipStack}>
                        <span className={styles.chipName}>{chosenName}</span>
                        <span className={styles.chipSub}>{trainingLine(training)}</span>
                        <span className={styles.progTrack} aria-hidden="true">
                            <i style={{width: `${training.percent ?? 0}%`}} />
                        </span>
                    </span>
                ) : (
                    <span className={styles.chipName}>{chosenName}</span>
                )}
                <svg
                    className={styles.chipChevron}
                    width="10"
                    height="10"
                    viewBox="0 0 10 10"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <path d="M2 3.75 L5 6.75 L8 3.75" />
                </svg>
            </button>
        )
    }

    if (asked) {
        // The slot asks once (board step 5). Her pictures are delivered rows: letting her go erases her and her
        // photos, not what she made.
        return (
            <div className={styles.ask} role="alertdialog" aria-label={`Let ${asked.name} go?`}>
                <span className={styles.askText}>
                    Delete {asked.name}? <span>You keep the pictures you made.</span>
                </span>
                <button type="button" className={styles.keep} onClick={() => setAsked(null)} disabled={letting}>
                    Cancel
                </button>
                <button type="button" className={styles.letGo} onClick={() => letGo(asked)} disabled={letting}>
                    {letting ? 'Deleting…' : 'Delete'}
                </button>
                {deleteFetcher.data?.status === 'error' && (
                    <p className={styles.error} aria-live="polite">Could not let her go — try again.</p>
                )}
            </div>
        )
    }

    const ordered = orderForStrip(roster ?? [], chosenSlug)
    const {own, roster: house} = splitStrip(ordered)
    const overflow = ordered.length > STRIP_PAGE
    const more = ordered.length - STRIP_PAGE

    function cell(m: MuseRosterEntry) {
        const isChosen = m.slug === chosenSlug
        const isHighlighted = (highlighted ?? chosenSlug) === m.slug
        // A muse of her own that is not ready cannot be chosen yet: shown, with her percent.
        const unready = m.own === true && m.state !== 'ready'
        const cooking = m.own === true && m.state === 'training'
        const thumbClass = [styles.thumb, isHighlighted && styles.thumbOn, cooking && styles.cook].filter(Boolean).join(' ')
        return (
            <span key={m.slug} className={styles.cell}>
                <button
                    type="button"
                    className={thumbClass}
                    aria-pressed={isChosen}
                    aria-busy={pendingSlug === m.slug}
                    aria-label={unready ? `${m.name} — ${m.state === 'training' ? `being made, ${m.percent ?? 0}%` : 'not available'}` : m.name}
                    disabled={unready}
                    title={unready ? (m.state === 'training' ? `${m.name} · being made · ${m.percent ?? 0}%` : m.name) : m.name}
                    onMouseEnter={() => setHighlighted(m.slug)}
                    onFocus={() => setHighlighted(m.slug)}
                    onPointerDown={(e) => pressStart(m, e)}
                    onPointerUp={pressEnd}
                    onPointerCancel={pressEnd}
                    onPointerLeave={pressEnd}
                    onClick={(e) => {
                        if (longPressRef.current.fired) {
                            longPressRef.current.fired = false
                            e.preventDefault()
                            return
                        }
                        void choose(m.slug, m.name, m.thumb || null)
                    }}
                >
                    <MuseFrame src={m.thumb} alt="" className={styles.thumbFrame} />
                    {cooking && (
                        <span className={styles.cookTrack} aria-hidden="true">
                            <i style={{width: `${m.percent ?? 0}%`}} />
                        </span>
                    )}
                </button>
                {canLetGo(m) && (
                    <button
                        type="button"
                        className={styles.x}
                        aria-label={`Delete ${m.name}`}
                        onMouseEnter={() => setHighlighted(m.slug)}
                        onClick={() => setAsked(m)}
                    >
                        ×
                    </button>
                )}
            </span>
        )
    }

    return (
        <div className={styles.strip}>
            <div
                className={styles.track}
                ref={trackRef}
                onScroll={handleScroll}
                // The highlight lets go here, not on each thumb: cleared per thumb, the frame fell back
                // to the chosen muse every time the pointer crossed the gap between two thumbs.
                onMouseLeave={() => setHighlighted(null)}
                onBlur={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setHighlighted(null)
                }}
                role="group"
                aria-label="Choose her"
            >
                {onOpenIntake && (
                    // The + frame heads the strip (board step 1): the same 44x58, dashed. It opens the wall's intake.
                    <button
                        type="button"
                        className={`${styles.thumb} ${styles.plus}`}
                        aria-label="Make your own muse"
                        title="Make your own muse"
                        onClick={() => {
                            ovidTrack('model_cta_click', {source: 'muse_strip'})
                            onOpenIntake()
                        }}
                    >
                        +
                    </button>
                )}
                {own.map(cell)}
                {own.length > 0 && house.length > 0 && <span className={styles.rule} aria-hidden="true" />}
                {house.map(cell)}
            </div>
            {overflow && !atEnd && (
                <button type="button" className={styles.more} onClick={scrollMore} aria-label="More muses">
                    +{more}
                </button>
            )}
            {failed && <p className={styles.error} aria-live="polite">Could not choose her — try again.</p>}
        </div>
    )
}
